import {without} from 'lodash';
import React, {Component} from 'react';
import {connect} from 'react-redux';

import {AsyncStatusesEnum} from '../../../../../../libs/shared/core/src/helpers/types/asyncStatuses';
import {
  buildCancelTokenSource,
  CancelError,
  CancelToken,
  CancelTokenSource,
} from '../../../../../../libs/shared/core/src/helpers/utilities/asyncCancelHelpers';
import {
  ApiUploadedAttachments,
  AttachmentModel,
} from '../../../../../../libs/shared/core/src/models/api/attachmentModel';
import {ChannelModel} from '../../../../../../libs/shared/core/src/models/api/channelModel';
import {InboxModel} from '../../../../../../libs/shared/core/src/models/api/inboxModel';
import {fetchApiAsync} from '../../../../../../libs/shared/core/src/services/http/http';
import {
  DataEventTrackingContextType,
  withDataEventTracking,
} from '../../../../../../libs/shared/core/src/services/tracking/dataEventTrackingContext';
import {DataEventTrackingProvider} from '../../../../../../libs/shared/core/src/services/tracking/dataEventTrackingProvider';
import {ViewerPanelId} from '../../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {
  AsyncData,
  asyncDataFailed,
  asyncDataLoaded,
  asyncDataLoading,
} from '../../../../../../libs/shared/ui/src/components/loaders/asyncDataHelpers';
import {makeMapStateToProps} from '../../../../../../srcCommon/helpers/reselectHelpers';
import {
  ActivityModel,
  DetailedActivityConversationLinkedModel,
} from '../../../../../models/api/activityModel';
import {ConversationModel} from '../../../../../models/api/conversationModel';
import {CustomAttributeModel, CustomFieldModel} from '../../../../../models/api/customFieldModel';
import {ApiSeenByList, SeenByModel} from '../../../../../models/api/seenByModel';
import {AppState} from '../../../../../store/appState';
import {Dispatch} from '../../../../../store/appStore';
import {fetchPinnedActivitiesAsyncAction} from '../../../../../store/data/activities/activitiesActions';
import {getCompanyHost} from '../../../../../store/data/company/companySelectors';
import {
  makeGetConversationChannels,
  makeGetConversationCustomAttributes,
  makeGetConversationInboxes,
  makeGetConversationPinnedActivities,
} from '../../../../../store/data/conversations/conversationsSelectors';
import {
  makeGetConversationLinkedConversations,
  makeGetOriginalConversationFromActivities,
} from '../../../../../store/data/conversations/linkedConversations/linkedConversationsSelectors';
import {getHasLinkedConversations} from '../../../../../store/data/conversations/linkedConversationsSelectors';
import {
  getCustomFieldsForConversationsFlat,
  getHasConversationCustomFields,
} from '../../../../../store/data/customFields/customFieldsSelectors';
import {FindTeammate, getFindTeammate} from '../../../../../store/data/teammates/teammatesSelectors';
import {navigateToLinkAction} from '../../../../../store/stateless/navigationActions';
import {ConversationSummary} from './conversationSummary';
import {ConversationSummarySectionsEnum} from './conversationSummarySections';

/*
 * Props + State.
 */

interface ConversationSummaryContainerPublicProps {
  panelId: ViewerPanelId;
  conversation: ConversationModel;
  onRequestClose: () => void;
  teammateId: number;
  selectedTopicId?: number;
  initiallyExpandedSection?: ConversationSummarySectionsEnum;
}

interface ConversationSummaryContainerProps extends ConversationSummaryContainerPublicProps {
  dispatch: Dispatch;
  host: string;
  inboxes: ReadonlyArray<InboxModel>;
  channels: ReadonlyArray<ChannelModel>;
  customFields: ReadonlyArray<CustomFieldModel>;
  findTeammate: FindTeammate;
  pinnedActivities: ReadonlyArray<ActivityModel>;
  linkedConversations: ReadonlyArray<DetailedActivityConversationLinkedModel>;
  hasLinkedConversations: boolean;
  hasConversationCustomFields: boolean;
  customAttributes: ReadonlyArray<CustomAttributeModel>;
  originalConversation?: ConversationModel;
}

interface ConversationSummaryContainerState {
  pinnedActivitiesAsyncData: AsyncData<ReadonlyArray<ActivityModel>>;
  attachments: AsyncData<ReadonlyArray<AttachmentModel>>;
  seenBy: AsyncData<ReadonlyArray<SeenByModel>>;
  unsavedCustomAttributes: ReadonlyArray<CustomAttributeModel>;
  expandedSections: ReadonlyArray<ConversationSummarySectionsEnum>;
}

type ConversationSummaryContainerPropsWithTracking = ConversationSummaryContainerProps &
  DataEventTrackingContextType;

/*
 * Component.
 */

class ConversationSummaryContainerComponent extends Component<
  ConversationSummaryContainerPropsWithTracking,
  ConversationSummaryContainerState
> {
  private readonly fetchCancelSource: CancelTokenSource;
  private get cancelToken(): CancelToken {
    return this.fetchCancelSource.token;
  }

  constructor(props: ConversationSummaryContainerPropsWithTracking) {
    super(props);

    this.fetchCancelSource = buildCancelTokenSource();

    this.state = {
      pinnedActivitiesAsyncData: {status: AsyncStatusesEnum.LOADING},
      attachments: {status: AsyncStatusesEnum.LOADING},
      seenBy: {status: AsyncStatusesEnum.LOADING},
      unsavedCustomAttributes: [],
      expandedSections: props.initiallyExpandedSection ? [props.initiallyExpandedSection] : [],
    };
  }

  /*
   * Lifecycle.
   */

  componentDidMount() {
    this.fetchPinnedActivitiesAsync();
    this.fetchSeenByAsync();
    this.fetchAttachmentsAsync();
  }

  componentWillUnmount() {
    this.fetchCancelSource.cancel();
  }

  /*
   * Network fetches.
   */

  private readonly fetchPinnedActivitiesAsync = async () => {
    this.setState({pinnedActivitiesAsyncData: asyncDataLoading()});

    try {
      await this.props.dispatch(fetchPinnedActivitiesAsyncAction(this.props.conversation, this.cancelToken));

      // Actual pinnedActivities are added to pinnedActivitiesAsyncData in `buildPinnedActivities` after retrieving from Redux.
      this.setState({pinnedActivitiesAsyncData: asyncDataLoaded([])});
    } catch (error) {
      if (error instanceof CancelError) {
        return;
      }

      this.setState({pinnedActivitiesAsyncData: asyncDataFailed(error)});
    }
  };

  private readonly buildPinnedActivities = (): AsyncData<ReadonlyArray<ActivityModel>> => {
    if (this.state.pinnedActivitiesAsyncData.status !== AsyncStatusesEnum.LOADED) {
      return this.state.pinnedActivitiesAsyncData;
    }

    return {
      status: AsyncStatusesEnum.LOADED,
      data: this.props.pinnedActivities,
    };
  };

  private readonly buildLinkedConversations = (): AsyncData<
    ReadonlyArray<DetailedActivityConversationLinkedModel>
  > => {
    if (this.state.pinnedActivitiesAsyncData.status !== AsyncStatusesEnum.LOADED) {
      // Returns pinnedActivitiesAsyncData because we use it for the loading/error states. The same fetch is used for both pinned activities and linked conversations
      return this.state.pinnedActivitiesAsyncData;
    }

    return {
      status: AsyncStatusesEnum.LOADED,
      data: this.props.linkedConversations,
    };
  };

  private readonly fetchAttachmentsAsync = async () => {
    this.setState({attachments: asyncDataLoading()});

    try {
      const path = `${this.props.conversation.url}/attachments`;
      const attachmentsData = await fetchApiAsync(ApiUploadedAttachments, path, {
        cancelToken: this.cancelToken,
      });

      this.setState({attachments: asyncDataLoaded(attachmentsData)});
    } catch (error) {
      if (error instanceof CancelError) {
        return;
      }

      this.setState({attachments: asyncDataFailed(error)});
    }
  };

  private readonly fetchSeenByAsync = async () => {
    this.setState({seenBy: asyncDataLoading()});

    try {
      const path = `${this.props.conversation.url}/seen_by`;
      const seenByData = await fetchApiAsync(ApiSeenByList, path, {cancelToken: this.cancelToken});

      this.setState({seenBy: asyncDataLoaded(seenByData)});
    } catch (error) {
      if (error instanceof CancelError) {
        return;
      }

      this.setState({seenBy: asyncDataFailed(error)});
    }
  };

  private readonly addCustomAttribute = (customField: CustomFieldModel) => {
    this.setState(({unsavedCustomAttributes}) => ({
      unsavedCustomAttributes: [...unsavedCustomAttributes, {customField, value: ''}],
    }));
  };

  private readonly discardUnsavedCustomAttribute = (discardedCustomField: CustomFieldModel) => {
    this.setState(({unsavedCustomAttributes}) => ({
      unsavedCustomAttributes: unsavedCustomAttributes.filter(
        ({customField}) => customField.id !== discardedCustomField.id,
      ),
    }));
  };

  private readonly onToggleSection = (section: ConversationSummarySectionsEnum) => {
    this.setState(({expandedSections}) => ({
      expandedSections: expandedSections.includes(section)
        ? without(expandedSections, section)
        : [...expandedSections, section],
    }));
  };

  private readonly onLinkedConversationViewAllClick = () => {
    const {conversation, originalConversation} = this.props;
    const selectedConversation = originalConversation ?? conversation;
    if (!selectedConversation.allLinkedConversationsUrl) {
      return;
    }

    this.props.dispatch(navigateToLinkAction(selectedConversation.allLinkedConversationsUrl));
    this.props.onRequestClose();
  };

  /*
   * Render.
   */

  render() {
    return (
      <DataEventTrackingProvider context={{name: 'conversation_summary', payload: {}}}>
        <ConversationSummary
          panelId={this.props.panelId}
          teammateId={this.props.teammateId}
          onRequestClose={this.props.onRequestClose}
          initiallyExpandedSection={this.props.initiallyExpandedSection}
          expandedSections={this.state.expandedSections}
          onToggleSection={this.onToggleSection}
          conversation={this.props.conversation}
          host={this.props.host}
          inboxes={this.props.inboxes}
          channels={this.props.channels}
          customFields={this.props.customFields}
          findTeammate={this.props.findTeammate}
          attachments={this.state.attachments}
          seenBy={this.state.seenBy}
          selectedTopicId={this.props.selectedTopicId}
          onSeenByRetryClick={this.fetchSeenByAsync}
          onAttachmentsRetryClick={this.fetchAttachmentsAsync}
          pinnedActivities={this.buildPinnedActivities()}
          linkedConversations={this.buildLinkedConversations()}
          onPinnedActivitiesRetryClick={this.fetchPinnedActivitiesAsync}
          hasConversationCustomFields={this.props.hasConversationCustomFields}
          customAttributes={this.props.customAttributes}
          unsavedCustomAttributes={this.state.unsavedCustomAttributes}
          onAddCustomAttribute={this.addCustomAttribute}
          onDiscardUnsavedCustomAttribute={this.discardUnsavedCustomAttribute}
          hasLinkedConversations={this.props.hasLinkedConversations}
          onLinkedConversationViewAllClick={this.onLinkedConversationViewAllClick}
          trackDataEvent={this.props.trackDataEvent}
        />
      </DataEventTrackingProvider>
    );
  }
}

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<ConversationSummaryContainerPublicProps>()(() => {
  const getConversation = (_state: AppState, props: ConversationSummaryContainerPublicProps) =>
    props.conversation;
  const getConversationLinkedConversations = makeGetConversationLinkedConversations(getConversation);

  return {
    inboxes: makeGetConversationInboxes(getConversation),
    channels: makeGetConversationChannels(getConversation),
    customFields: getCustomFieldsForConversationsFlat,
    pinnedActivities: makeGetConversationPinnedActivities(getConversation),
    linkedConversations: getConversationLinkedConversations,
    hasConversationCustomFields: getHasConversationCustomFields,
    customAttributes: makeGetConversationCustomAttributes(getConversation),
    host: getCompanyHost,
    findTeammate: getFindTeammate,
    hasLinkedConversations: getHasLinkedConversations,
    originalConversation: makeGetOriginalConversationFromActivities(getConversationLinkedConversations),
  };
});

export const ConversationSummaryContainer = connect(mapStateToProps)(
  withDataEventTracking(ConversationSummaryContainerComponent),
);
